/* Iceberg */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Paper, Typography } from '@mui/material';

/* Workato */
import { SaasOpsKebabMenu } from 'application/Home/Admin/Workato/Components/SaasOpsKebabMenu';
import { getWorkatoStatus } from 'application/Home/Admin/Workato/functions';
import {
    WorkatoActions,
    WorkatoStatus
} from 'application/Home/Admin/Workato/types';
import {
    StringCell,
    UsageChartCell,
    UserBubblesCell,
    moreThanXUsers,
    userCountFilter
} from 'application/Shared/Components/Cells';
import { getDateTime } from 'application/Shared/Components/Core';
import { EmptyState } from 'application/Shared/Components/Dashboard/Panels/EmptyPanel';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { sortByRanking } from 'application/Shared/Components/Table/tableSortingFunctions';
import {
    CSVColumn,
    ColumnDef,
    Toggle,
    ToggleState
} from 'application/Shared/Components/Table/types';
import { buildColumns } from 'application/Shared/Components/Table/utils/buildColumn';
import { licenseTypeFilter } from 'application/Shared/Components/TableColumnFilters/LicenseTypesFilter';
import { TABLENAMES } from 'application/Shared/Constants';

/*  Shared  */
import { Helpers, Notify, Route } from 'application/Shared/Helpers';
import { Application } from 'application/Shared/types/application';
import { TenantApp } from 'application/Shared/types/tenantApp';
import {
    TenantJobStatusCodes,
    TenantJobType
} from 'application/Shared/types/tenantJob';
import { TenantLicenseUsage } from 'application/Shared/types/tenantLicenseUsage';
import { User } from 'application/Shared/types/user';
import { useFetchRecipes } from 'hooks/dataAccess/Recipe/recipe';
import { useFetchTenantJob } from 'hooks/dataAccess/TenantJob/tenantJob';

/* Hooks */
import { useFetchTenantLicenseUsage } from 'hooks/dataAccess/TenantLicenseUsage/tenantLicenseUsage';
import { useRunCustomerRecipe } from 'hooks/dataAccess/Workato';
import { getFullName } from 'objects/User';

/*  React  */
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { IntegrationAppUserDataCards } from '../internal/IntegrationAppUserDataCards';
import { LICENSE_METADATA_COLUMNS } from '../internal/LicenseMetadataColumns';

const integrationTableColumns = ({
    application
}: {
    application: Application;
}): ColumnDef<TenantLicenseUsage, string>[] => [
    {
        accessorKey: 'email',
        cell: StringCell,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'accounts',
        name: 'Common.account',
        size: 300,
        testId: 'accounts'
    },
    {
        accessorKey: 'externalUserId',
        cell: StringCell,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'userId',
        name: 'Common.userId',
        size: 300,
        testId: 'userId'
    },
    {
        accessorKey: 'licenseTier+skuName',
        cell: StringCell,
        filterFn: licenseTypeFilter,
        header: Header,
        id: 'licenseType',
        meta: {
            filter: {
                defaultOptions: [],
                includeNullValue: true
            }
        },
        name: 'Common.licenseType',
        size: 300,
        testId: 'licenseType'
    },
    {
        accessorKey: 'accounts',
        cell: (props) => <UserBubblesCell {...props} dataIsUser />,
        filterFn: moreThanXUsers,
        header: Header,
        id: 'userCount',
        meta: { filter: userCountFilter },
        name: 'Common.discovered_users',
        size: 300,
        testId: 'userCount',
        tooltipText: 'Users.expired'
    },
    {
        accessorKey: 'actions',
        cell: ({ row }) => (
            <SaasOpsKebabMenu
                application={application}
                dataTrackId="licenseTab"
                userId={
                    row.original.platformUserId || row.original.accounts[0]?.id
                }
                userEmail={row.original.email}
            />
        ),
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 10,
        meta: {
            align: 'right',
            header: {
                justifyContent: 'flex-end'
            }
        },
        name: 'Common.actions',
        size: 1,
        testId: 'actions'
    },
    {
        accessorKey: 'usageChart',
        cell: UsageChartCell,
        enableColumnFilter: false,
        header: Header,
        id: 'usageChart',
        name: 'Common.usage_chart',
        size: 300,
        sortingFn: sortByRanking,
        testId: 'usageChart'
    }
];

const LastIntegrationUpdate = ({
    message,
    setMessage
}: {
    message: ReactNode;
    setMessage: (element: ReactNode) => void;
}) => {
    const { t } = useTranslation();
    const [lastDateSynced, setLastDateSynced] = useState<Date | null>(null);
    const { rowData: data } = useManagedTableV2Context<TenantLicenseUsage>();

    useEffect(() => {
        if (!data) return;
        const dates = data.map(
            (item) => new Date(item['tenantLicenseTier+dateLastSynced'])
        );
        if (!dates.length) return;
        const lastDateSyncedUpdated = dates.reduce((max, date) =>
            date > max ? date : max
        );
        setLastDateSynced(lastDateSyncedUpdated);
    }, [data]);

    useEffect(() => {
        setMessage(
            <>
                {t('Applications.integration.lastUpdate', {
                    date: lastDateSynced
                        ? getDateTime({ date: lastDateSynced })
                        : t('Common.unavailable')
                })}
            </>
        );
    }, [setMessage, lastDateSynced, t]);

    return (
        <Box display="flex" data-testid="tableSimpleHeader" width="100%">
            <Typography
                alignItems="center"
                variant="span"
                display="inline-flex"
            >
                {message}
            </Typography>
        </Box>
    );
};

// This toggle controls the visibility of disabled/expired users in the table
// When enabled, it filters out disabled users from the view client-side.
// When enabled, it also impacts the unused accounts calulcation
// in the IntegrationAppUserDataCards component.
const initialToggles: Array<Toggle> = [
    {
        defaultEnabled: true,
        forceParameter: true,
        key: 'includeUser',
        parameter: { field: 'includeUser', value: false },
        toggleText: 'Exclude Disabled Users',
        tooltip: 'Users.expired'
    }
];

export const IntegrationUsersPage = ({
    tenantApp
}: {
    tenantApp: TenantApp;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const [message, setMessage] = useState<ReactNode>(null);
    const [updateInProgress, setUpdateInProgress] = useState(false);
    const [dynamicColumns, setDynamicColumns] = useState<
        ColumnDef<TenantLicenseUsage, string>[]
    >([]);
    const autoupdateInterval = useRef<NodeJS.Timeout | null>(null);
    const prevTenantLicenseUsage = useRef<TenantLicenseUsage[]>([]);

    const [recipeId, setRecipeId] = useState<string | null>(null);
    const [jobId, setJobId] = useState<string | undefined>();
    const [refreshError, setRefreshError] = useState(false);
    const [sortKey, setSortKey] = useState('accounts');
    const [toggles, setToggles] = useState<ToggleState>({});
    const [rowData, setRowData] = useState<TenantLicenseUsage[]>([]);

    const showNotification = useRef(false);

    const { workatoConnection } = tenantApp;
    const workatoConfigured =
        getWorkatoStatus(workatoConnection) === WorkatoStatus.connected;

    const pollingInterval = 5000;
    const { application } = tenantApp;
    const { id: applicationId } = application;
    const { runRecipe } = useRunCustomerRecipe();

    const recipeParams = useMemo(
        () => ({
            action: WorkatoActions.license,
            applicationId,
            includeApplicationId: true,
            platformTenantId: clientId!,
            size: 1
        }),
        [applicationId, clientId]
    );

    const inProgressJobParams = useMemo(
        () => ({
            clientId: clientId!,
            entityId: recipeId!,
            status: TenantJobStatusCodes.InProgress,
            type: TenantJobType.Workato
        }),
        [clientId, recipeId]
    );

    const tenantLicenseUsageParams = useMemo(
        () => ({
            applicationId,
            clientId: clientId!
        }),
        [applicationId, clientId]
    );

    const { recipes } = useFetchRecipes(recipeParams);
    const { tenantLicenseUsage, refetchData, loading, refetching } =
        useFetchTenantLicenseUsage(tenantLicenseUsageParams);
    const {
        tenantJob: inProgressTenantJob,
        refetchData: refetchInProgressTenantJob
    } = useFetchTenantJob(inProgressJobParams);

    const {
        tenantJob: completedTenantJob,
        refetchData: refetchCompletedTenantJob
    } = useFetchTenantJob({
        clientId: clientId!,
        id: jobId,
        type: TenantJobType.Workato
    });

    const initialLoad = loading && !refetching;

    // Update row data when API data changes (initial load or refresh)
    // This runs only when the API data is updated
    useEffect(() => {
        if (!loading && !refetching) {
            setRowData(tenantLicenseUsage);
        }
    }, [tenantLicenseUsage, loading, refetching]);

    // Client-side filtering based on toggle state
    // Instead of making API calls when toggles change, we filter the existing data
    // This approach provides immediate feedback and reduces server load
    useEffect(() => {
        if (!Object.keys(toggles).length || !tenantLicenseUsage.length) return;
        const includeUserToggle = toggles.includeUser;
        if (includeUserToggle?.enabled) {
            // Create a transformed version of each row with filtered accounts
            const rowsWithFilteredAccounts = tenantLicenseUsage.map((row) => {
                // Create a shallow copy of the row
                const newRow = { ...row };
                // Filter out disabled accounts
                newRow.accounts = row.accounts.filter(
                    (account) => !account.disabled
                );
                return newRow;
            });

            // Update the row data with the filtered accounts
            setRowData(rowsWithFilteredAccounts);
        } else {
            setRowData(tenantLicenseUsage);
        }
    }, [toggles, tenantLicenseUsage]);
    // Set the recipe id
    useEffect(() => {
        if (recipes.length) {
            setRecipeId(recipes[0].id);
        }
    }, [recipes]);

    // Set the job id from the pending job so that we can poll for the completed job
    useEffect(() => {
        if (inProgressTenantJob && !updateInProgress && !jobId) {
            setJobId(inProgressTenantJob.id);
            setUpdateInProgress(true);
        }
    }, [inProgressTenantJob, updateInProgress, jobId]);

    // Start a polling the job until it is completed
    useEffect(() => {
        if (updateInProgress && !autoupdateInterval.current && jobId) {
            autoupdateInterval.current = setInterval(() => {
                refetchCompletedTenantJob();
            }, pollingInterval);
        } else if (!updateInProgress && autoupdateInterval.current) {
            clearInterval(autoupdateInterval.current);
            autoupdateInterval.current = null;
        }
    }, [updateInProgress, refetchCompletedTenantJob, jobId]);

    // Refetch the data when the job is completed
    useEffect(() => {
        if (
            completedTenantJob &&
            completedTenantJob.status !== TenantJobStatusCodes.InProgress &&
            updateInProgress &&
            !loading
        ) {
            refetchInProgressTenantJob();
            refetchData();
            setJobId(undefined); // Reset jobId after completion
        }
    }, [
        completedTenantJob,
        refetchData,
        refetchInProgressTenantJob,
        updateInProgress,
        loading
    ]);

    // Compare the previous and current data to determine if there is a change and show a notification
    useEffect(() => {
        const prev = prevTenantLicenseUsage.current;
        // Skip notification for initial load
        if (!showNotification.current) {
            showNotification.current = true;
            return;
        }

        // Skip if we're still loading
        if (loading) {
            return;
        }

        // Handle initial data load or empty data
        if (!prev?.length) {
            // Update previous data regardless of whether it's empty or not
            prevTenantLicenseUsage.current = tenantLicenseUsage;
            return;
        }

        // Only show notification if we're not in the initial load state
        if (showNotification.current) {
            const dataChanged = !Helpers.arraysEqual(prev, tenantLicenseUsage, [
                'tenantLicenseTier+dateLastSynced'
            ]);

            // Handle completed update
            if (refreshError) {
                Notify.e(t('LicenseData.status.sync.error'));
            } else if (dataChanged) {
                Notify.s(t('LicenseData.status.sync.success'));
            } else {
                Notify.i(t('LicenseData.status.sync.noNewData'));
            }

            setUpdateInProgress(false);
            showNotification.current = false;
        }

        // Update previous data after showing notification
        prevTenantLicenseUsage.current = tenantLicenseUsage;
    }, [tenantLicenseUsage, loading, refreshError, t]);

    useEffect(() => {
        const columns = buildColumns(
            LICENSE_METADATA_COLUMNS,
            integrationTableColumns({ application }),
            tenantLicenseUsage
        );
        const isThereALastLoginTimeColumn = !!columns.find(
            (col) => col.id === 'lastLoginTime'
        );
        if (isThereALastLoginTimeColumn) {
            setSortKey('lastLoginTime');
        }
        setDynamicColumns(columns);
    }, [setDynamicColumns, tenantLicenseUsage, application]);

    const baseParameters = useMemo(
        () => ({
            applicationId,
            includeAccounts: true,
            includeApplication: true,
            includeClients: true,
            platformTenantId: clientId,
            size: -1
        }),
        [applicationId, clientId]
    );

    const columnVisibility = useMemo(
        () => ({
            licenseType: true,
            userCount: true
        }),
        []
    );

    const columnPinning = {
        left: ['accounts'],
        right: ['actions']
    };

    const csvFields: CSVColumn[] = useMemo(
        () => [
            {
                key: 'email',
                label: t('Common.account')
            },
            {
                key: 'externalUserId',
                label: t('Common.userId')
            },
            {
                id: 'licenseType',
                key: 'licenseTier+skuName',
                label: t('Common.licenseType')
            },
            {
                fn: (value: unknown) =>
                    (value as User[]).map(getFullName).join(', '),
                id: 'userCount',
                key: 'userAccounts',
                label: t('Common.users'),
                rowKeyAndDataPath: 'accounts',
                transform: 'custom'
            },
            {
                id: 'lastLoginTime',
                key: 'lastLoginTime',
                label: t('Common.lastLoginTime'),
                rowKeyAndDataPath: 'metadata:lastLoginTime',
                transform: 'objectExtract'
            },
            {
                id: 'usageChart',
                key: 'usage',
                label: t('Common.usage'),
                transform: 'hms'
            }
            // The following columns will be added in the future to recipes. Once they are added,
            // we can uncomment the columns and display the data.
            // {
            //     key: 'mfa',
            //     label: t('Common.mfa'),
            //     rowKeyAndDataPath: 'metadata:mfa',
            //     transform: 'objectExtract'
            // },
            // {
            //     key: 'role',
            //     label: t('Users.role'),
            //     rowKeyAndDataPath: 'metadata:role',
            //     transform: 'objectExtract'
            // }
        ],
        [t]
    );

    const handleRefresh = async () => {
        setUpdateInProgress(true);
        const { data, error } = await runRecipe({
            platformTenantId: clientId!,
            recipeId: recipeId!
        });
        if (data?.jobId) {
            setJobId(data.jobId);
        } else if (error) {
            setUpdateInProgress(false);
            setRefreshError(true);
        } else {
            setRefreshError(false);
        }
    };

    const { partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    return (
        <ManagedTableV2ContextProvider
            baseParameters={baseParameters}
            columns={dynamicColumns}
            externalLoading={initialLoad}
            ignoreSearchParams
            initialState={{
                columnPinning,
                columnVisibility
            }}
            key={sortKey}
            mode="client"
            rows={rowData}
            rowsPerPage={10}
            searchEnabled
            sortDirection="ASC"
            sortKey={sortKey}
            tableName={TABLENAMES.INTEGRATION_USERS}
            toggles={initialToggles}
            setToggles={setToggles}
        >
            <IntegrationAppUserDataCards />
            <Paper elevation={0} variant="outlined" sx={{ mt: '24px', p: 0 }}>
                <ManagedTableV2UI
                    newDataLoading={updateInProgress}
                    csvExport
                    csvExportFields={csvFields}
                    csvExportFileName="license_user_breakdown"
                    emptyRowsMessage={
                        !workatoConfigured
                            ? ((
                                  <EmptyState
                                      content="SaasOps.emptyState.message"
                                      actions={[
                                          {
                                              href: `${baseAdminRoute}/saasOpsIntegrations`,
                                              label: 'SaasOps.emptyState.setUpIntegration'
                                          }
                                      ]}
                                      image="calendar"
                                  />
                              ) as unknown as string)
                            : undefined
                    }
                    enableShowHideColumns
                    message={
                        !updateInProgress &&
                        workatoConfigured && (
                            <LastIntegrationUpdate
                                message={message}
                                setMessage={setMessage}
                            />
                        )
                    }
                    onRefresh={workatoConfigured ? handleRefresh : undefined}
                    renderFooter
                    refreshTooltip={t(
                        'LicenseData.refreshButton.disabled.tooltip'
                    )}
                    titleDef={{ titleKey: 'Common.accountsWithCount' }}
                />
            </Paper>
        </ManagedTableV2ContextProvider>
    );
};
